import React, { useMemo, useRef } from 'react';
import qs from 'query-string';
import { ProductGrid } from '../../components';
import { ContentGridContainer } from '../../components/ContentGridContainer';
import { useCatalogProductsIndexController, useShopRootController } from './CatalogProductsIndex.controller';
import { Seo } from '@shared/components/Seo';
import { Redirect, useLocation, useRouteMatch, useTranslation, StaticRouterProps } from '@shared/core';
import { CatalogSearchHeader } from '../../components/CatalogSearchHeader';
import { Page } from '../../components/LayoutStructure';
import { useCatalogContext } from '../../state/Catalog';
import { RecommendedCategoriesSection } from './components/RecommendedCategoriesSection/RecommendedCategoriesSection';
import RecommendedProductsSection from './components/RecommendedProductsSection';
import { ScrollToTop } from '@apps/common/components/ScrollToTop/ScrollToTop';
import { BrandsMarquee } from './components/BrandsMarquee';
import ValuePropsSection from './components/ValuePropsSection';
import { TelemetryProvider as RegistryGiftCountTelemetryProvider, Page as TelemetryPage } from '@apps/registry/common/components/RegistryGiftCounts/telemetry';
import { CategoryFeaturesSection } from '../../components/CategoryFeaturesSection';
import { StyledCategoryTitle } from '../../components/SelectedCategoryText/SelectedCategoryText';
import { RegistryGiftCountsShop } from '../../components/RegistryGiftCountsShop';
import { useCatalogRoutePaths } from '@apps/registry/common/components/Catalog/Catalog.routes';
import { useShopStorefrontContext } from '../../components/ShopStorefrontProvider';
import { CatalogStorefront, EventType } from '@graphql/generated';
import { BottomCategoryShowcase } from './components/BottomCategoryShowcase';
import { CatalogCurationHero } from '../CurationsIndex/components/CurationHeroHeader/CatalogCurationHero';
import { useEventTypeConfig } from '@shared/utils/eventType';
import { Box } from '@withjoy/joykit';
import { UniversalProductSearch } from '../../components/UniversalProductSearch';
import { SearchProductsProvider } from './components/SearchProductsProvider/SearchProductsProvider';
import { getUniversalSearchTermFromCategory } from '../../hooks/useUniversalSearchFeature';
import CatalogCarousel from '../../components/Carousel/CatalogCarousel/CatalogCarousel';
import { useRefinementsPanelProvider } from '../../components/RefinementsPanel';
import { deriveQueryStringHelpersFromLocation } from '@shared/core/queryString';

interface CatalogProductsIndexProps
  extends Readonly<{
    categoryLocator: Maybe<string>;
    heroContent?: React.ReactNode;
    staticContext?: StaticRouterProps['context'];
  }> {}

type ShopIndexProps = {
  eventHandle: Maybe<string>;
  eventId: Maybe<string>;
  eventType: Maybe<EventType>;
  categoryLocator: Maybe<string>;
};

const ShopIndex = (props: ShopIndexProps) => {
  const { categoryLocator, eventId, eventHandle, eventType } = props;
  const { currentStorefront } = useShopStorefrontContext();
  useShopRootController();

  return (
    <>
      <CatalogCurationHero />
      {eventId && eventHandle && currentStorefront !== CatalogStorefront.baby && eventType !== EventType.babyRegistry && (
        <RegistryGiftCountTelemetryProvider context={{ eventId }}>
          <RegistryGiftCountsShop
            eventId={eventId}
            page={TelemetryPage.shop}
            eventHandle={eventHandle}
          />
        </RegistryGiftCountTelemetryProvider>
      )}
      <Box
        marginTop={'1rem'}
        marginBottom={'5rem'}
      >
        <BrandsMarquee />
      </Box>
      <ValuePropsSection />
      <ContentGridContainer
        showSideBar={!!categoryLocator}
        flexDirection={'column'}
        key={currentStorefront}
      >
        <RecommendedProductsSection eventId={eventId} />
        <Box width={'100%'}>
          <RecommendedCategoriesSection eventId={eventId} />
        </Box>
      </ContentGridContainer>
    </>
  );
};

const ShopCatalogOrSearchResults = (props: CatalogProductsIndexProps & { searchTerm: Maybe<string> }) => {
  const {
    catalogSearchResults,
    fallbackCatalogProductList,
    filters,
    sortBy,
    isMobile,
    addCatalogItemToRegistry,
    addCatalogItemLoading,
    handleOnLoadMoreProductsButtonClick,
    isFetchingMore,
    gridTelemetryPageContext,
    isLoadingPrimaryCatalog,
    loading,
    showSkeleton,
    selectedCategory,
    searchTerm,
    hasNextPage,
    totalProductCount,
    showTopCategoryShowcase,
    verifyUserAccessAndInvokeCb,
    redirectPath,
    showUniversalSearch,
  } = useCatalogProductsIndexController({ ...props });
  // setup a toolkit by which querystring params will be propagated automatically on redirect
  const location = useLocation();
  const { buildRedirectPath, buildPartialLocation } = deriveQueryStringHelpersFromLocation(location);
  const { t } = useTranslation('catalogRegistry');
  const { exploreAllCategory, exploreAllProducts } = t('productGrid');
  const showValuePropCards = !searchTerm;
  const showValuePropsSection = !searchTerm;
  const showcaseExploreAllRef = useRef<HTMLDivElement>(null);

  // consistent UX -- if the Filters are "always open", keep them open during skeleton loading as well
  //   otherwise they cause a big re-render & reflow
  const { isOpen } = useRefinementsPanelProvider();
  const disableFilters = useMemo(() => loading && !isOpen, [loading, isOpen]);

  const { config } = useEventTypeConfig();

  const universalSearchQuery = searchTerm || getUniversalSearchTermFromCategory(selectedCategory);
  if (redirectPath) {
    // querystring params will be propagated automatically
    if (props.staticContext) {
      // only defined during SSR
      props.staticContext.url = buildRedirectPath(redirectPath);
    } else {
      // https://v5.reactrouter.com/web/api/Redirect
      const redirectLocation = buildPartialLocation(redirectPath);
      return <Redirect to={redirectLocation} />;
    }
  }

  return (
    <>
      {searchTerm ? (
        <CatalogSearchHeader
          totalFoundBySearch={totalProductCount}
          searchTerm={searchTerm}
          isLoading={isLoadingPrimaryCatalog}
        />
      ) : null}
      {showTopCategoryShowcase && (
        <>
          <CategoryFeaturesSection
            isMobile={isMobile}
            telemetryPageValue={gridTelemetryPageContext}
            sortBy={sortBy}
            showcaseExploreAllRef={showcaseExploreAllRef}
            placement="top"
          />
          <StyledCategoryTitle
            marginBottom={6}
            ref={showcaseExploreAllRef}
          >
            {selectedCategory ? exploreAllCategory({ categoryTitle: selectedCategory.title }) : exploreAllProducts()}
          </StyledCategoryTitle>
        </>
      )}
      <ProductGrid
        isMobile={isMobile}
        filters={filters}
        sortBy={sortBy}
        searchLoading={loading}
        showSkeleton={showSkeleton}
        disableFilters={disableFilters}
        categoryLocator={selectedCategory?.publicUrlPath}
        componentSource="productGrid"
        categoryType="category"
        telemetryPageValue={gridTelemetryPageContext}
        products={!!catalogSearchResults?.length ? catalogSearchResults : fallbackCatalogProductList}
        totalProductCount={totalProductCount}
        addCatalogItem={addCatalogItemToRegistry}
        addItemLoading={addCatalogItemLoading}
        isFetchingMore={isFetchingMore}
        onLoadMoreClick={handleOnLoadMoreProductsButtonClick}
        hasNextPage={hasNextPage}
        disableGridMarginBottom={false}
        shouldShowValuePropCards={showValuePropCards}
        shouldShowValuePropsSection={showValuePropsSection && !!selectedCategory && !showTopCategoryShowcase}
        hideHeader={showTopCategoryShowcase}
        shouldOpenPdp={true}
      />
      <CatalogCarousel
        isMobile={isMobile}
        telemetryPageValue={gridTelemetryPageContext}
        sortBy={sortBy}
        placement="bottom"
        showTitle={true}
      />
      {showUniversalSearch && universalSearchQuery && (
        <UniversalProductSearch
          isMobile={isMobile}
          onAddProductToRegistry={addCatalogItemToRegistry}
          searchQuery={universalSearchQuery}
          verifyUserAccessAndInvokeCb={verifyUserAccessAndInvokeCb}
          joyCatalogSearchResultCount={totalProductCount}
          visibleJoyCatalogSearchResultsCount={catalogSearchResults.length}
          telemetryPageValue={selectedCategory ? gridTelemetryPageContext : undefined}
          // 1. When there are search results, the universal search results grid will be placed below
          // 2. When there are no search results, we only show the universal search results
          placement={catalogSearchResults.length === 0 ? 'primary' : 'secondary'}
        />
      )}
      {config?.bottomCategoryShowcase && <BottomCategoryShowcase />}
    </>
  );
};

const CatalogSeo = ({ category }: { category: { title: string; publicUrlPath: string } | null }) => {
  const { currentStorefront } = useShopStorefrontContext();

  const { t } = useTranslation('catalogRegistry');

  const seoTrans = t('seo', currentStorefront);

  return category ? (
    <Seo
      title={seoTrans.shopCategoryTitle({ category: category.title.trim() })}
      description={seoTrans.shopCategoryDescription({ category: category.title.trim() })}
      canonicalURL={`https://withjoy.com${category?.publicUrlPath}/`}
    />
  ) : (
    <Seo
      title={seoTrans.shopIndexTitle()}
      description={seoTrans.shopIndexDescription()}
    />
  );
};

export const CatalogProductsIndex = (props: CatalogProductsIndexProps) => {
  const { catalogRoutes } = useCatalogRoutePaths();
  const isShopRootRouteMatch = useRouteMatch({ path: catalogRoutes.catalogIndex.paths, exact: true });

  const { eventId, eventType, eventHandle, selectedCategory } = useCatalogContext();

  const location = useLocation();
  let { s: searchTerm } = qs.parse(location.search);
  searchTerm = !!searchTerm?.length ? searchTerm.toString() : null;

  return (
    <Page>
      <ScrollToTop />

      <CatalogSeo category={selectedCategory} />
      <SearchProductsProvider>
        {!isShopRootRouteMatch || searchTerm ? (
          <ShopCatalogOrSearchResults
            {...props}
            searchTerm={searchTerm}
          />
        ) : (
          <ShopIndex
            eventHandle={eventHandle}
            eventId={eventId}
            categoryLocator={props.categoryLocator}
            eventType={eventType}
          />
        )}
      </SearchProductsProvider>
    </Page>
  );
};
